/**
 * People & Org Planning — AOP 2027 Planning (Anna, Oct 2026).
 *
 * Department leads add the hires they plan for 2027. The page is a copy of the Org Planning
 * Overview's layout — the same recursive org chart, department tabs, trimester columns and filters —
 * with the department leads' AOP drafts on it in purple. Drafts are filed through
 * `/api/aop-planning` into their own Airtable table (`AOP [proposed]`): no Slack, no Role Approvals card,
 * and the regular proposal flow is not touched at all. See `proxy-server/routes/aop-planning.js`.
 *
 * What it reuses from the overview it only IMPORTS, unchanged: the table renderer, the tab and
 * filter helpers and the viewer scope (a department lead acts only in their own line). "Edit" opens
 * the AOP edit form for a draft; on any other row (a hiring-plan role or a regular proposal) it says
 * where that row is edited instead. "Manage backfill" on a leaver opens the AOP form pre-filled for
 * the backfill; a leaver recorded as "not backfilled" on the overview is changed there.
 *
 * Columns: Jan–Sep 2026 collapsed into one, every month from October 2026 to December 2027 shown —
 * see `aopPlanning/aopColumns.ts`. The tree is read live, so a role approved meanwhile on the
 * overview shows here on the next load (or Refresh).
 *
 * Access is gated at the route level (`PeopleSpace.tsx`); the proxy re-authorizes every request.
 */
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarRange, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { CockpitTabs } from './taCockpit/cockpitTabs';
import { BLUE_BUTTON, BUTTON_CONTENT, RADIUS } from './orgPlanningChrome';
import { DataQualityBanner } from './internalPlayground/DataQualityBanner';
import { OrgChartTable } from './internalPlayground/OrgChartTable';
import type { BackfillPerson } from './internalPlayground/BackfillControl';
import { type BackfillTarget, formatDay } from './internalPlayground/backfillForm';
import { buildDepartmentTabs, slugifyDepartment } from './internalPlayground/departmentTabs';
import { TimelineFilterBar } from './internalPlayground/TimelineFilterBar';
import type { EditRoleTarget } from './internalPlayground/EditRoleModal';
import { ViewerScopeContext, viewerScopeFrom } from './internalPlayground/viewerScope';
import {
  defaultTimelineFilters,
  filterOrgTree,
  fteByMonthByNodeId,
  isFilteringActive,
  rolesInScope,
  todayIso,
} from './internalPlayground/timelineFilters';
import { InternalPlaygroundAccessError, type OrgNode } from '../services/internalPlaygroundApi';
import { AOP_APPROVALS_QUERY_KEY, AOP_SOURCE, AOP_TIMELINE_QUERY_KEY, fetchAopTimeline } from '../services/aopPlanningApi';
import { AopProposeModal } from './aopPlanning/AopProposeModal';
import { AopEditModal } from './aopPlanning/AopEditModal';
import { aopMonthGroups, defaultExpandedGroupKeys } from './aopPlanning/aopColumns';
import { AopTargetsTable } from './aopPlanning/AopTargetsTable';
import { AopCostChart } from './aopPlanning/AopCostCharts';
import { AopCompanyTargets } from './aopPlanning/AopCompanyTargets';
import { AopIterationBar } from './aopPlanning/AopIterationBar';
import { AopApprovalsTab } from './aopPlanning/AopApprovalsTab';

const BASE_PATH = '/aop-2027';
const OVERVIEW_TAB = 'overview';
const APPROVALS_TAB = 'approvals';

/** The page below the header: tabs, iteration bar, targets, the tree and its forms. */
function AopPlanningContent() {
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: AOP_TIMELINE_QUERY_KEY,
    queryFn: fetchAopTimeline,
    retry: (count, err) => !(err instanceof InternalPlaygroundAccessError) && count < 2,
    // Three Airtable scans plus the BigQuery employee read; Refresh forces it.
    staleTime: 5 * 60 * 1000,
  });
  const viewerScope = useMemo(() => viewerScopeFrom(data?.viewer), [data?.viewer]);
  // Manual expand/collapse exceptions and expanded trimesters — shared across tabs, as on the overview.
  const [toggledIds, setToggledIds] = useState<Set<string>>(new Set());
  const groups = useMemo(() => (data ? aopMonthGroups(data.months) : []), [data]);
  // `null` until the person toggles a group: until then the default (Oct 2026 on expanded) applies,
  // so it is right on the first render, before and after the data arrives.
  const [toggledGroupKeys, setToggledGroupKeys] = useState<Set<string> | null>(null);
  const expandedGroupKeys = useMemo(
    () => toggledGroupKeys ?? defaultExpandedGroupKeys(groups),
    [toggledGroupKeys, groups]
  );
  const [filters, setFilters] = useState(defaultTimelineFilters());
  const [adding, setAdding] = useState(false);
  const [editTarget, setEditTarget] = useState<EditRoleTarget | null>(null);
  const [backfillTarget, setBackfillTarget] = useState<BackfillTarget | null>(null);
  const [notBackfilled, setNotBackfilled] = useState<BackfillPerson | null>(null);
  // Departments whose submission the last draft change withdrew — said once the form has closed.
  const [withdrawNotice, setWithdrawNotice] = useState<string[] | null>(null);
  const openBackfill = (person: BackfillPerson) => {
    if (person.backfill.state === 'open') {
      setBackfillTarget({
        employeeId: person.employeeId,
        name: person.name,
        terminationDate: person.terminationDate,
        draft: person.backfill.draft,
      });
    } else {
      setNotBackfilled(person);
    }
  };
  const toggleGroup = (key: string) =>
    setToggledGroupKeys(() => {
      const next = new Set(expandedGroupKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const columns = useMemo(
    () => groups.flatMap((g) => (expandedGroupKeys.has(g.key) ? g.months : [g.key])),
    [groups, expandedGroupKeys]
  );

  const toggle = (id: string) =>
    setToggledIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" aria-label="Loading AOP 2027 planning" />
      </div>
    );
  }

  if (error instanceof InternalPlaygroundAccessError) {
    return (
      <div className="mx-auto mt-6 max-w-md rounded-md border border-zinc-200 bg-white p-4 text-sm text-zinc-600">
        You do not have access to this page.
      </div>
    );
  }

  // Only with nothing to show: a failed background refetch (after a sync, on focus) keeps the page.
  if (error && !data) {
    return (
      <div className="mx-auto mt-6 flex max-w-md items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
        <div className="text-sm text-red-800">
          <p className="font-medium">Couldn&apos;t load AOP 2027 planning</p>
          <p className="mt-0.5 text-red-700">{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  // The Approvals tab: the reviewers and Anna/David — the proxy refuses its data to anyone else.
  const seesApprovals = Boolean(data.iteration && (data.iteration.reviewerKey || data.iteration.canApprove));
  const tabs = [
    { value: OVERVIEW_TAB, label: 'Overview', node: data.root },
    ...(seesApprovals ? [{ value: APPROVALS_TAB, label: 'Approvals', node: data.root }] : []),
    ...buildDepartmentTabs(data.root),
  ];
  const activeTab = tabs.find((t) => t.value === tabParam);
  if (!activeTab) return <Navigate to={`${BASE_PATH}/${OVERVIEW_TAB}`} replace />;
  const isApprovals = seesApprovals && activeTab.value === APPROVALS_TAB;

  const goToTab = (value: string) => navigate(`${BASE_PATH}/${value}`);
  /** The Overview's department rows jump to that department's tab. */
  const goToDepartment = (node: OrgNode) => goToTab(slugifyDepartment(node.groupLabel ?? node.personName));

  // A real department — the proxy's `departmentsOf`: a person's branch, or CEO Office. A top-level
  // "Unassigned" branch gets a tab but nobody submits it, so it shows no submission state.
  const isDepartmentTab =
    activeTab.value !== OVERVIEW_TAB && !isApprovals && (activeTab.node.isPerson || activeTab.node.id === 'ceo_office');
  const iterationDepartment = isDepartmentTab ? activeTab.label : null;

  const roleOptions = rolesInScope(activeTab.node);
  const filteredRoot = filterOrgTree(activeTab.node, filters, todayIso());
  const unfilteredFteById = isFilteringActive(filters) ? fteByMonthByNodeId(activeTab.node) : undefined;

  return (
    <ViewerScopeContext.Provider value={viewerScope}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CockpitTabs
            value={activeTab.value}
            tabs={tabs}
            onChange={goToTab}
            shape="soft"
            ariaLabel="AOP 2027 planning sections"
          />
          <div className="flex items-center gap-2">
            {!isApprovals && <TimelineFilterBar filters={filters} onChange={setFilters} roleOptions={roleOptions} />}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={BLUE_BUTTON}
              contentClassName={BUTTON_CONTENT}
              onClick={() => setAdding(true)}
            >
              <Plus className="h-4 w-4" />
              Add 2027 hires
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={BLUE_BUTTON}
              contentClassName={BUTTON_CONTENT}
              onClick={() => {
                void refetch();
                // The Approvals tab's drafts, too — Refresh must never leave them behind the status.
                void queryClient.invalidateQueries({ queryKey: AOP_APPROVALS_QUERY_KEY });
              }}
              loading={isFetching}
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {error && !isFetching && (
          <p role="alert" className="text-xs text-red-700">
            Could not refresh the page ({error instanceof Error ? error.message : 'unknown error'}) — it shows the last
            loaded state. Try Refresh again.
          </p>
        )}

        {data.iteration ? (
          // Keyed by tab, so a refusal on one department never shows on the next.
          <AopIterationBar key={activeTab.value} status={data.iteration} department={iterationDepartment} />
        ) : (
          data.dataQuality.iterationUnavailable && (
            <p role="alert" className="text-xs text-amber-700">
              {data.dataQuality.iterationUnavailable} Submitting is unavailable for now.
            </p>
          )
        )}

        {withdrawNotice && (
          <div
            role="status"
            className={`${RADIUS} flex items-start gap-3 border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800`}
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <p className="flex-1">
              Your change withdrew the submission of {withdrawNotice.join(' and ')}
              {data.iteration?.current ? ` for ${data.iteration.current.name}` : ''}. The department head needs to submit
              again.
            </p>
            <Button type="button" size="sm" variant="ghost" onClick={() => setWithdrawNotice(null)}>
              Dismiss
            </Button>
          </div>
        )}

        {/* At the TOP, unlike the data-quality footnote: the drafts are what this page is for, and a
            page without them would tell a department lead their hires were lost. */}
        {data.dataQuality.aopDraftsUnavailable && (
          <div
            role="alert"
            className={`${RADIUS} flex items-start gap-3 border border-red-200 bg-red-50 p-3 text-sm text-red-800`}
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <p>
              The AOP drafts could not be loaded, so they are missing from this page. Nothing is lost —
              refresh in a moment, and tell Anna if it keeps happening.
            </p>
          </div>
        )}

        {data.dataQuality.cacUnavailable && (
          <div role="alert" className={`${RADIUS} flex items-start gap-3 border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800`}>
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <p>
              Which roles are booked as CAC could not be read, so CAC roles may be counted on this page right
              now. Refresh in a moment, and tell Anna if it keeps happening.
            </p>
          </div>
        )}

        {data.dataQuality.targetsUnavailable && (
          <div className={`${RADIUS} flex items-start gap-3 border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800`}>
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <p>{data.dataQuality.targetsUnavailable}</p>
          </div>
        )}

        {/* A department's targets, only where the proxy sent them — see AopTargetsTable. */}
        {activeTab.value !== OVERVIEW_TAB && !isApprovals && data.targets?.[activeTab.label] && (
          <>
            <AopTargetsTable
              label={activeTab.label}
              targets={data.targets[activeTab.label]}
              columns={columns}
              groups={groups}
              expandedGroupKeys={expandedGroupKeys}
            />
            <AopCostChart label={activeTab.label} months={data.targets[activeTab.label].months} groups={groups} />
          </>
        )}

        {/* The company, only for a viewer who sees every department — see AopCompanyTargets. */}
        {activeTab.value === OVERVIEW_TAB && data.company && (
          <AopCompanyTargets
            company={data.company}
            targets={data.targets ?? {}}
            columns={columns}
            groups={groups}
            expandedGroupKeys={expandedGroupKeys}
            onSelectDepartment={(label) => goToTab(slugifyDepartment(label))}
          />
        )}

        {isApprovals && data.iteration ? (
          <AopApprovalsTab status={data.iteration} root={data.root} />
        ) : filteredRoot ? (
          <OrgChartTable
            root={filteredRoot}
            columns={columns}
            groups={groups}
            expandedGroupKeys={expandedGroupKeys}
            onToggleGroup={toggleGroup}
            toggledIds={toggledIds}
            onToggle={toggle}
            maxDepth={activeTab.value === OVERVIEW_TAB ? 1 : Infinity}
            onDeadEndClick={activeTab.value === OVERVIEW_TAB ? goToDepartment : undefined}
            flat={activeTab.value === OVERVIEW_TAB}
            unfilteredFteById={unfilteredFteById}
            onBackfillOpen={openBackfill}
            edit={{ onEdit: setEditTarget }}
          />
        ) : (
          <div className="rounded-md border border-zinc-200 bg-white p-6 text-center text-sm text-zinc-500">
            Nothing matches these filters in {activeTab.label}.
          </div>
        )}

        {/* The unfiltered tree for the manager picker: a filter must not narrow who a role can go to. */}
        {adding && <AopProposeModal root={data.root} onClose={() => setAdding(false)} onWithdrawn={setWithdrawNotice} />}
        {backfillTarget && (
          <AopProposeModal
            root={data.root}
            backfill={backfillTarget}
            onClose={() => setBackfillTarget(null)}
            onWithdrawn={setWithdrawNotice}
          />
        )}
        {notBackfilled && <NotBackfilledHere person={notBackfilled} onClose={() => setNotBackfilled(null)} />}

        {editTarget && (editTarget.source as string) === AOP_SOURCE && (
          <AopEditModal
            recordId={editTarget.recordId}
            label={editTarget.label}
            root={data.root}
            onClose={() => setEditTarget(null)}
            onWithdrawn={setWithdrawNotice}
          />
        )}
        {editTarget && (editTarget.source as string) !== AOP_SOURCE && (
          <NotAnAopDraft label={editTarget.label} onClose={() => setEditTarget(null)} />
        )}

        <p className="text-xs text-zinc-400">
          CAC is not part of the AOP: CAC people and roles are left out of this page (a CAC manager stays
          as a row with no FTE). Purple rows are AOP drafts and proposals — not committed yet, counted
          apart from committed FTE.
          FTE = weekly working hours ÷ 40 for hired people; planned roles default to 1.0 (0.5 for Working
          Students) from their start month onward. Generated {new Date(data.generatedAt).toLocaleString()}.
        </p>

        <DataQualityBanner dataQuality={data.dataQuality} />
      </div>
    </ViewerScopeContext.Provider>
  );
}

/** A leaver already settled as "not backfilled" — that is recorded (and changed) on the overview. */
function NotBackfilledHere({ person, onClose }: { person: BackfillPerson; onClose: () => void }) {
  const state = person.backfill;
  const why =
    state.state === 'not_backfilled' && state.reason === 'decided'
      ? `It was recorded as not backfilled${state.decidedBy ? ` by ${state.decidedBy}` : ''}${
          state.decidedAt ? ` on ${formatDay(state.decidedAt.slice(0, 10))}` : ''
        }.`
      : state.state === 'not_backfilled' && state.reason === 'proposal_declined'
        ? 'Its backfill proposal was declined.'
        : 'They have left and no backfill was recorded.';
  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !w-[min(100vw-2rem,28rem)] gap-4 rounded p-6">
        <ModalHeader>
          <ModalTitle className="!text-lg !font-semibold">{person.name}</ModalTitle>
        </ModalHeader>
        <p className="text-sm text-zinc-600">
          {why} To plan a backfill after all, change it on the Org Planning Overview first — then it can be
          planned here.
        </p>
        <div className="flex justify-end">
          <Button type="button" size="sm" onClick={onClose}>
            OK
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}

/** "Edit" on a row that is not an AOP draft: this page only changes drafts. */
function NotAnAopDraft({ label, onClose }: { label: string; onClose: () => void }) {
  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !w-[min(100vw-2rem,28rem)] gap-4 rounded p-6">
        <ModalHeader>
          <ModalTitle className="!text-lg !font-semibold">{label}</ModalTitle>
        </ModalHeader>
        <p className="text-sm text-zinc-600">
          This role is already in the hiring plan or the proposals, not an AOP draft. Change it on the Org
          Planning Overview — this page only edits the 2027 hires added here.
        </p>
        <div className="flex justify-end">
          <Button type="button" size="sm" onClick={onClose}>
            OK
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}

export default function AopPlanningView() {
  return (
    <div className="mx-auto max-w-[1400px] px-4 py-8">
      <header className="mb-6 flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-zinc-900">
          <CalendarRange className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">AOP 2027 Planning</h1>
          <p className="text-sm text-zinc-500">
            Add the hires your department plans for 2027. Drafts are purple and go to nobody for approval yet.
          </p>
        </div>
      </header>
      <AopPlanningContent />
    </div>
  );
}
